import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Check, ChevronRight, Search } from "lucide-react";
import { toast } from "sonner";

import type { TFunction } from "@/i18n/catalog";
import { useTranslation } from "@/i18n/use-translation";
import { parseLocaleNumber } from "@/lib/format";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useApiHeaders } from "@/hooks/use-api-headers";
import {
  useCreateWasteEvent,
  useUpdateWasteEvent,
  type CreateWasteInput,
  type UpdateWasteInput,
} from "@/hooks/use-waste-event-actions";
import { useKitchens, type SiteSummary } from "@/hooks/use-active-menus";
import { useDevices } from "@/hooks/use-devices";
import { itemDisplayName } from "@/lib/inventory";
import { foodLabel } from "@/lib/food-labels";
import type { WasteEvent } from "@/hooks/use-waste-events";
import {
  ListSkeleton,
  OptionList,
  useFoodCategories,
  useInventoryItems,
  useSiteInventoryListId,
  type FoodCategory,
} from "@/components/waste/item-picker";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

const WEIGHT_PRESETS = [0.5, 1.0, 2.0, 5.0] as const;

/** Literal keys in a lookup table — a key built at runtime is invisible to `check-i18n.mjs`. */
const WASTE_TYPES: { value: string; labelKey: string }[] = [
  { value: "overproduction", labelKey: "reference.wasteType.overproduction" },
  { value: "spoilage", labelKey: "reference.wasteType.spoilage" },
  { value: "preparation", labelKey: "reference.wasteType.preparation" },
  { value: "post_consumer", labelKey: "reference.wasteType.post_consumer" },
  { value: "packaging", labelKey: "reference.wasteType.packaging" },
  { value: "unassigned", labelKey: "reference.wasteType.unassigned" },
];

// §10.9 step 1 — station defaults the waste reason (spec §6.2 mapping).
const LOCATIONS: { value: string; labelKey: string }[] = [
  { value: "prep_area", labelKey: "waste.locationPrepArea" },
  { value: "service_line", labelKey: "waste.locationServiceLine" },
  { value: "wash_up", labelKey: "waste.locationWashUp" },
  { value: "cold_store", labelKey: "waste.locationColdStore" },
];

export function reasonForLocation(loc: string): string {
  if (loc === "prep_area") return "preparation";
  if (loc === "service_line") return "overproduction";
  if (loc === "wash_up") return "post_consumer";
  if (loc === "cold_store") return "spoilage";
  return "unassigned";
}

type MenuDishItem = {
  id: string;
  name: string;
  category_id: string | null;
  subcategory_id: string | null;
};
type MenuDish = { id: string; name: string; items: MenuDishItem[] };

// What the staff member picked — drives the payload + the default waste_type.
export type Selection = {
  kind: "item" | "dish" | "category";
  label: string;
  category_id?: string;
  subcategory_id?: string;
  inventory_item_id?: string;
  recipe_id?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  sites: SiteSummary[];
  defaultSiteId?: string | null;
  defaultKitchenId?: string | null;
  // When set, the dialog edits this manual event instead of creating one.
  editEvent?: WasteEvent | null;
};

// <input type="datetime-local"> works in local time; convert to/from ISO.
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function nowLocalInput(): string {
  return toLocalInput(new Date().toISOString());
}

// Rebuild a picker Selection from an existing event so edit pre-fills it.
function selectionFromEvent(t: TFunction, e: WasteEvent): Selection | null {
  if (e.inventory_item_id) {
    return {
      kind: "item",
      label:
        foodLabel(t, e.inventory_item_name) ?? foodLabel(t, e.subcategory_name) ?? t("waste.item"),
      category_id: e.category_id ?? undefined,
      subcategory_id: e.subcategory_id ?? undefined,
      inventory_item_id: e.inventory_item_id,
    };
  }
  if (e.recipe_id) return { kind: "dish", label: t("waste.menuDish"), recipe_id: e.recipe_id };
  if (e.subcategory_id) {
    return {
      kind: "category",
      label: foodLabel(t, e.subcategory_name) ?? "",
      category_id: e.category_id ?? undefined,
      subcategory_id: e.subcategory_id,
    };
  }
  if (e.category_id)
    return {
      kind: "category",
      label: foodLabel(t, e.category_name) ?? "",
      category_id: e.category_id,
    };
  return null;
}

export function defaultWasteType(kind: Selection["kind"]): string {
  if (kind === "dish") return "overproduction";
  if (kind === "item") return "spoilage";
  return "unassigned";
}

// Human-identified depth → tier: specific item/dish = L4, sub-category = L3,
// category-only = L2. (No-menu fallback lands at category level.)
export function classificationLevel(s: Selection): number {
  if (s.inventory_item_id || s.recipe_id) return 4;
  if (s.subcategory_id) return 3;
  return 2;
}

function useActiveMenuDishes(kitchenId: string | null, enabled: boolean, date: string) {
  const headers = useApiHeaders();
  // `date` is the event's "When" date so the picker offers the menu active on
  // that date — matching what classification resolves for the event (POS2-733).
  const queryDate = date || new Date().toISOString().slice(0, 10);
  return useQuery<MenuDish[]>({
    queryKey: ["active-menu-dishes", kitchenId, queryDate],
    queryFn: async () => {
      const res = await fetch(
        `${API_URL}/api/v1/kitchens/${kitchenId}/active-menu?date=${queryDate}`,
        { headers },
      );
      if (!res.ok) throw new Error(`Failed to load menu: ${res.status}`);
      return (await res.json()).data.dishes as MenuDish[];
    },
    enabled: enabled && !!kitchenId,
  });
}

export function LogWasteDialog({
  open,
  onClose,
  sites,
  defaultSiteId,
  defaultKitchenId,
  editEvent,
}: Props) {
  const { locale, t } = useTranslation();
  const create = useCreateWasteEvent();
  const update = useUpdateWasteEvent();
  const isEdit = !!editEvent;

  const [siteId, setSiteId] = useState<string | null>(defaultSiteId ?? null);
  const [kitchenId, setKitchenId] = useState<string | null>(defaultKitchenId ?? null);
  const [location, setLocation] = useState<string | null>(null);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [wasteType, setWasteType] = useState<string>("unassigned");
  const [weight, setWeight] = useState<string>("");
  const [loggedAt, setLoggedAt] = useState<string>(nowLocalInput());
  const [deviceSerial, setDeviceSerial] = useState<string | null>(null);
  const [notes, setNotes] = useState<string>("");
  const [justLogged, setJustLogged] = useState(false);

  const kitchensQuery = useKitchens(siteId);
  const kitchens = kitchensQuery.data ?? [];
  // Scope the device list to the selected site (via its kitchens), narrowing to a chosen
  // kitchen — else the dropdown lists every site's devices (POS2-851).
  const siteKitchenIds = new Set(kitchens.map((k) => k.id));
  const devices = (useDevices().data ?? []).filter((d) =>
    kitchenId ? d.kitchen_id === kitchenId : !siteId || siteKitchenIds.has(d.kitchen_id ?? ""),
  );
  const pending = create.isPending || update.isPending;
  const eventDate = loggedAt.slice(0, 10); // YYYY-MM-DD for the active-menu lookup

  // Seed on (re)open — from the event being edited, else the page filters.
  useEffect(() => {
    if (!open) return;
    if (editEvent) {
      setSiteId(editEvent.site_id);
      setKitchenId(editEvent.kitchen_id);
      setLocation(null);
      setSelection(selectionFromEvent(t, editEvent));
      setWasteType(editEvent.waste_type ?? "unassigned");
      setWeight(String(editEvent.item_weight));
      setLoggedAt(toLocalInput(editEvent.logged_at));
      setDeviceSerial(editEvent.device_serial ?? null);
      setNotes(editEvent.ai_reasoning ?? "");
      setJustLogged(false);
    } else {
      setSiteId(defaultSiteId ?? null);
      setKitchenId(defaultKitchenId ?? null);
      resetEntry();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editEvent?.id]);

  function resetEntry() {
    setLocation(null);
    setSelection(null);
    setWeight("");
    setWasteType("unassigned");
    setLoggedAt(nowLocalInput());
    setDeviceSerial(null);
    setNotes("");
    setJustLogged(false);
  }

  function onLocationChange(loc: string) {
    setLocation(loc);
    setWasteType(reasonForLocation(loc)); // §10.9 step 1 — default the reason
  }

  function pick(next: Selection) {
    setSelection(next);
    setWasteType(defaultWasteType(next.kind));
  }

  // `Number.parseFloat("1,5")` is 1 — it stops at the comma and reports success, so a German
  // kitchen would have logged 1 kg where they meant 1.5 (pinned decision 3). The input is
  // `type="text"` below for the same reason: a number input refuses a comma outright.
  const weightNum = parseLocaleNumber(locale, weight) ?? Number.NaN;
  const canSubmit =
    !!kitchenId &&
    !!selection &&
    Number.isFinite(weightNum) &&
    weightNum > 0 &&
    !!loggedAt &&
    !pending;

  function handleSubmit() {
    if (!canSubmit || !selection || !kitchenId) return;
    const isoTime = new Date(loggedAt).toISOString();

    if (editEvent) {
      const body: UpdateWasteInput = {
        eventId: editEvent.id,
        item_weight: weightNum,
        logged_at: isoTime,
        classification_level: classificationLevel(selection),
        category_id: selection.category_id ?? null,
        subcategory_id: selection.subcategory_id ?? null,
        inventory_item_id: selection.inventory_item_id ?? null,
        recipe_id: selection.recipe_id ?? null,
        waste_type: wasteType,
        device_serial: deviceSerial,
        ai_reasoning: notes.trim() || null,
      };
      update.mutate(body, {
        onSuccess: () => {
          toast.success(t("waste.updated"));
          onClose();
        },
        onError: () => t("waste.updateFailed"),
      });
      return;
    }

    const body: CreateWasteInput = {
      kitchen_id: kitchenId,
      item_weight: weightNum,
      logged_at: isoTime,
      source: "manual",
      status: "successful",
      is_confirmed: true,
      classification_level: classificationLevel(selection),
      category_id: selection.category_id ?? null,
      subcategory_id: selection.subcategory_id ?? null,
      inventory_item_id: selection.inventory_item_id ?? null,
      recipe_id: selection.recipe_id ?? null,
      waste_type: wasteType,
      device_serial: deviceSerial,
      ai_reasoning: notes.trim() || null,
    };
    create.mutate(body, {
      onSuccess: () => {
        toast.success(t("waste.logged"));
        setJustLogged(true);
      },
      onError: () => t("waste.logFailed"),
    });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="flex max-h-[90vh] max-w-lg flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-sm font-medium text-gray-900">
            {isEdit ? t("waste.editTitle") : t("waste.logTitle")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isEdit ? t("waste.editDescription") : t("waste.logDescription")}
          </DialogDescription>
        </DialogHeader>

        {justLogged ? (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <div className="flex size-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <Check className="size-6" aria-hidden />
            </div>
            <p className="text-sm text-gray-700">{t("waste.eventLogged")}</p>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={resetEntry} data-testid="log-another">
                {t("waste.logAnother")}
              </Button>
              <Button onClick={onClose}>{t("waste.done")}</Button>
            </div>
          </div>
        ) : (
          <div className="-mr-2 flex flex-1 flex-col gap-4 overflow-y-auto pr-2">
            {/* Location: site full-width, then kitchen + optional device */}
            <div className="flex flex-col gap-2">
              <Select
                value={siteId ?? ""}
                onValueChange={(v) => {
                  setSiteId(v);
                  setKitchenId(null);
                }}
              >
                <SelectTrigger className="h-10 text-sm" data-testid="site-select">
                  <SelectValue placeholder={t("waste.site")} />
                </SelectTrigger>
                <SelectContent>
                  {sites.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-2">
                <Select
                  value={kitchenId ?? ""}
                  onValueChange={setKitchenId}
                  disabled={!siteId || kitchens.length === 0}
                >
                  <SelectTrigger className="h-10 text-sm" data-testid="kitchen-select">
                    <SelectValue placeholder={t("waste.kitchen")} />
                  </SelectTrigger>
                  <SelectContent>
                    {kitchens.map((k) => (
                      <SelectItem key={k.id} value={k.id}>
                        {k.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={deviceSerial ?? "none"}
                  onValueChange={(v) => {
                    const serial = v === "none" ? null : v;
                    setDeviceSerial(serial);
                    // Prefill the station from the device's location (overridable),
                    // which also defaults the waste reason. POS2-733.
                    const dev = devices.find((d) => d.device_serial === serial);
                    if (dev?.location) {
                      setLocation(dev.location);
                      setWasteType(reasonForLocation(dev.location));
                    }
                  }}
                >
                  <SelectTrigger className="h-10 text-sm" data-testid="device-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("waste.noDevice")}</SelectItem>
                    {devices.map((d) => (
                      <SelectItem key={d.id} value={d.device_serial}>
                        {d.name ?? d.device_serial}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Location / station — defaults the waste reason (§10.9 step 1) */}
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-gray-500">{t("waste.location")}</span>
              <Select value={location ?? ""} onValueChange={onLocationChange}>
                <SelectTrigger className="h-10 text-sm" data-testid="location-select">
                  <SelectValue placeholder={t("waste.locationPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {LOCATIONS.map((l) => (
                    <SelectItem key={l.value} value={l.value}>
                      {t(l.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* When it happened — chosen before the item so the Menu-dish
                picker offers the menu active on that date. */}
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-gray-500">{t("waste.when")}</span>
              <Input
                type="datetime-local"
                value={loggedAt}
                onChange={(e) => setLoggedAt(e.target.value)}
                className="h-10 text-sm"
                data-testid="logged-at-input"
              />
            </div>

            {/* Item selection */}
            {selection ? (
              <div className="flex items-center justify-between gap-2 rounded-md border border-black/[0.06] bg-gray-50 px-3 py-2">
                <span className="truncate text-sm text-gray-800" data-testid="selected-item">
                  {selection.label}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setSelection(null)}
                >
                  {t("waste.change")}
                </Button>
              </div>
            ) : (
              <ItemSelector
                siteId={siteId}
                kitchenId={kitchenId}
                eventDate={eventDate}
                onPick={pick}
              />
            )}

            {/* Weight */}
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-gray-500">{t("waste.weight")}</span>
              <div className="flex flex-wrap gap-2">
                {WEIGHT_PRESETS.map((p) => (
                  <Button
                    key={p}
                    type="button"
                    variant={weight === String(p) ? "default" : "secondary"}
                    size="sm"
                    className="h-9 min-w-14"
                    onClick={() => setWeight(String(p))}
                  >
                    {p}
                  </Button>
                ))}
              </div>
              <Input
                type="text"
                inputMode="decimal"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder={t("waste.weightPlaceholder")}
                className="h-10 text-sm"
                data-testid="weight-input"
              />
            </div>

            {/* Waste type (auto-derived, overridable) */}
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-gray-500">{t("waste.wasteType")}</span>
              <Select
                value={wasteType}
                onValueChange={setWasteType}
                disabled={!selection && !location}
              >
                <SelectTrigger className="h-10 text-sm" data-testid="waste-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WASTE_TYPES.map((w) => (
                    <SelectItem key={w.value} value={w.value}>
                      {t(w.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes (optional) */}
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-gray-500">{t("waste.notes")}</span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("waste.notesPlaceholder")}
                rows={2}
                className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                data-testid="notes-input"
              />
            </div>

            <Button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="h-11"
              data-testid="log-waste-submit"
            >
              {pending ? t("waste.saving") : isEdit ? t("waste.saveChanges") : t("waste.logTitle")}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Item selector: Stock item drill (+ category-only) or Menu dish ────────

function ItemSelector({
  siteId,
  kitchenId,
  eventDate,
  onPick,
}: {
  siteId: string | null;
  kitchenId: string | null;
  eventDate: string;
  onPick: (s: Selection) => void;
}) {
  const { t } = useTranslation();
  const [source, setSource] = useState<"stock" | "dish">("stock");
  // The "Menu dish" tab is available whenever the active-menu resolver returns
  // dishes for the event date — an uploaded/seeded site menu OR the
  // platform-default fallback (POS2-733). Not gated on menu_sites.is_active,
  // which never counts the platform default.
  const dishesQuery = useActiveMenuDishes(kitchenId, true, eventDate);
  const dishes = dishesQuery.data ?? [];
  const hasActiveMenu = dishes.length > 0;

  return (
    <div className="flex flex-col gap-2">
      {hasActiveMenu && (
        <div className="flex gap-1 rounded-md bg-gray-100 p-1 text-xs">
          <button
            type="button"
            onClick={() => setSource("stock")}
            className={`flex-1 rounded px-2 py-1.5 ${source === "stock" ? "bg-white shadow-sm" : "text-gray-500"}`}
            data-testid="source-stock"
          >
            {t("waste.stockItem")}
          </button>
          <button
            type="button"
            onClick={() => setSource("dish")}
            className={`flex-1 rounded px-2 py-1.5 ${source === "dish" ? "bg-white shadow-sm" : "text-gray-500"}`}
            data-testid="source-dish"
          >
            {t("waste.menuDish")}
          </button>
        </div>
      )}

      {source === "dish" && hasActiveMenu ? (
        <DishPicker
          key={eventDate}
          dishes={dishes}
          isLoading={dishesQuery.isLoading}
          onPick={onPick}
        />
      ) : (
        <StockPicker siteId={siteId} onPick={onPick} />
      )}
    </div>
  );
}

function DishPicker({
  dishes,
  isLoading,
  onPick,
}: {
  dishes: MenuDish[];
  isLoading: boolean;
  onPick: (s: Selection) => void;
}) {
  const { t } = useTranslation();
  // Two-step drill: pick a dish, then one of its ingredient items, so the
  // selection lands on an inventory_item_id (L4 with an attached item, like the
  // Stock path). POS2-733.
  const [dish, setDish] = useState<MenuDish | null>(null);
  const [search, setSearch] = useState("");
  const norm = search.trim().toLowerCase();
  const matches = (label: string) => label.toLowerCase().includes(norm);

  return (
    <>
      {dish && (
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <button
            type="button"
            onClick={() => {
              setSearch("");
              setDish(null);
            }}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label={t("waste.back")}
          >
            <ArrowLeft className="size-3.5" aria-hidden />
          </button>
          <span className="text-gray-900">{dish.name}</span>
        </div>
      )}

      <SearchBox
        value={search}
        onChange={setSearch}
        placeholder={dish ? t("waste.searchItems") : t("waste.searchDishes")}
      />
      <div className="max-h-60 overflow-y-auto">
        {isLoading ? (
          <ListSkeleton />
        ) : dish ? (
          <OptionList
            options={dish.items.filter((i) => matches(foodLabel(t, i.name)))}
            empty={t("waste.noItemsOnDish")}
            showChevron={false}
            onSelect={(i) =>
              onPick({
                kind: "item",
                label: i.name,
                inventory_item_id: i.id,
                category_id: i.category_id ?? undefined,
                subcategory_id: i.subcategory_id ?? undefined,
                recipe_id: dish.id,
              })
            }
            render={(i) => foodLabel(t, i.name)}
          />
        ) : (
          <OptionList
            options={dishes.filter((d) => matches(d.name))}
            empty={t("waste.noDishesToday")}
            showChevron
            onSelect={(d) => {
              setDish(d);
              setSearch("");
            }}
            render={(d) => d.name}
          />
        )}
      </div>
    </>
  );
}

function StockPicker({
  siteId,
  onPick,
}: {
  siteId: string | null;
  onPick: (s: Selection) => void;
}) {
  const { t } = useTranslation();
  const [category, setCategory] = useState<FoodCategory | null>(null);
  const [subcategory, setSubcategory] = useState<FoodCategory | null>(null);
  const [search, setSearch] = useState("");

  const categoriesQuery = useFoodCategories(true);
  const listQuery = useSiteInventoryListId(siteId, true);
  const itemsQuery = useInventoryItems(listQuery.data ?? null, subcategory?.id ?? null);

  const all = categoriesQuery.data ?? [];
  const top = all.filter((c) => !c.parent_id);
  const subs = category ? all.filter((c) => c.parent_id === category.id) : [];
  const step: "category" | "subcategory" | "item" = !category
    ? "category"
    : !subcategory
      ? "subcategory"
      : "item";
  const norm = search.trim().toLowerCase();
  const matches = (label: string) => label.toLowerCase().includes(norm);

  function goBack() {
    setSearch("");
    if (step === "item") setSubcategory(null);
    else if (step === "subcategory") setCategory(null);
  }

  return (
    <>
      <div className="flex items-center gap-1.5 text-xs text-gray-500">
        {step !== "category" && (
          <button
            type="button"
            onClick={goBack}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label={t("waste.back")}
          >
            <ArrowLeft className="size-3.5" aria-hidden />
          </button>
        )}
        <span className={step === "category" ? "text-gray-900" : ""}>{t("waste.category")}</span>
        {category && (
          <>
            <ChevronRight className="size-3 text-gray-300" aria-hidden />
            <span className={step === "subcategory" ? "text-gray-900" : ""}>
              {foodLabel(t, category.name)}
            </span>
          </>
        )}
        {subcategory && (
          <>
            <ChevronRight className="size-3 text-gray-300" aria-hidden />
            <span className="text-gray-900">{foodLabel(t, subcategory.name)}</span>
          </>
        )}
      </div>

      {/* Commit at the current level without drilling to a specific item. */}
      {step === "subcategory" && category && (
        <Button
          variant="secondary"
          size="sm"
          className="h-9 justify-start text-xs"
          onClick={() =>
            onPick({
              kind: "category",
              label: foodLabel(t, category.name),
              category_id: category.id,
            })
          }
          data-testid="use-category"
        >
          {t("waste.useCategoryOnly", { name: foodLabel(t, category.name) })}
        </Button>
      )}
      {step === "item" && category && subcategory && (
        <Button
          variant="secondary"
          size="sm"
          className="h-9 justify-start text-xs"
          onClick={() =>
            onPick({
              kind: "category",
              label: foodLabel(t, subcategory.name),
              category_id: category.id,
              subcategory_id: subcategory.id,
            })
          }
          data-testid="use-subcategory"
        >
          {t("waste.useSubcategoryOnly", { name: foodLabel(t, subcategory.name) })}
        </Button>
      )}

      <SearchBox
        value={search}
        onChange={setSearch}
        placeholder={t("waste.searchStep", {
          what:
            step === "item"
              ? t("waste.stepItems")
              : step === "subcategory"
                ? t("waste.stepSubcategories")
                : t("waste.stepCategories"),
        })}
      />

      <div className="max-h-60 overflow-y-auto">
        {step === "category" &&
          (categoriesQuery.isLoading ? (
            <ListSkeleton />
          ) : (
            <OptionList
              options={top.filter((c) => matches(foodLabel(t, c.name)))}
              empty={t("waste.noCategoriesMatch")}
              showChevron
              onSelect={(c) => {
                setCategory(c);
                setSearch("");
              }}
              render={(c) => foodLabel(t, c.name)}
            />
          ))}
        {step === "subcategory" && (
          <OptionList
            options={subs.filter((c) => matches(foodLabel(t, c.name)))}
            empty={t("waste.noSubcategories")}
            showChevron
            onSelect={(c) => {
              setSubcategory(c);
              setSearch("");
            }}
            render={(c) => foodLabel(t, c.name)}
          />
        )}
        {step === "item" &&
          category &&
          subcategory &&
          (itemsQuery.isLoading ? (
            <ListSkeleton />
          ) : (
            <OptionList
              options={(itemsQuery.data ?? []).filter(
                (i) => matches(foodLabel(t, itemDisplayName(i))) || matches(i.description),
              )}
              empty={t("waste.noItemsUseSubcategory")}
              showChevron={false}
              onSelect={(i) =>
                onPick({
                  kind: "item",
                  label: foodLabel(t, itemDisplayName(i)),
                  category_id: category.id,
                  subcategory_id: subcategory.id,
                  inventory_item_id: i.id,
                })
              }
              render={(i) => foodLabel(t, itemDisplayName(i))}
            />
          ))}
      </div>
    </>
  );
}

function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative">
      <Search
        className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-gray-300"
        aria-hidden
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-10 pl-9 text-sm"
      />
    </div>
  );
}
